import { useTranslation } from "react-i18next";
import { Zap, ExternalLink } from "lucide-react";
import { formatDateLong } from "@/lib/formatters";
import type { BillingStatus } from "../api/billing.api";

interface CurrentPlanCardProps {
  data: BillingStatus;
  onManageBilling: () => void;
  isPending: boolean;
}

const statusColors: Record<string, string> = {
  active: "bg-success/10 text-success",
  past_due: "bg-amber-500/10 text-amber-500",
  canceled: "bg-danger/10 text-danger",
};

/**
 * Displays the user's current plan with status badge,
 * renewal date, and manage billing button.
 */
export function CurrentPlanCard({ data, onManageBilling, isPending }: CurrentPlanCardProps) {
  const { t } = useTranslation();

  const statusLabels: Record<string, string> = {
    active: t("billing.active"),
    past_due: t("billing.pastDue"),
    canceled: t("billing.canceled"),
  };

  return (
    <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo to-cyan shadow-md">
            <Zap size={22} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted uppercase font-semibold tracking-wider">
                {t("billing.currentPlan")}
              </span>
            </div>
            <h2 className="text-xl font-bold text-heading">
              {data.plan.interval
                ? `${data.plan.name} ${
                    data.plan.interval === "annual"
                      ? t("billing.intervalAnnual")
                      : t("billing.intervalMonthly")
                  }`
                : data.plan.name}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[data.plan.billing_status] || "bg-muted/10 text-muted"}`}
              >
                {statusLabels[data.plan.billing_status] || "Active"}
              </span>
              {data.plan.billing_cycle_anchor && (
                <span className="text-xs text-muted flex items-center gap-1.5 font-medium">
                  <span>•</span>
                  <span>
                    {t("billing.renewsOn", {
                      date: formatDateLong(data.plan.billing_cycle_anchor),
                    })}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
        {data.plan.tier !== "free" && (
          <button
            type="button"
            onClick={onManageBilling}
            disabled={isPending}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-all hover:border-border-light hover:text-heading hover:bg-elevated/50 disabled:opacity-50"
          >
            <ExternalLink size={14} />
            {t("billing.manageBilling")}
          </button>
        )}
      </div>
    </div>
  );
}
